import React from 'react';
import { UserResult, Category, Feedback, Question } from '../types';
import { Visual } from './Visual';
import { Markdown } from './Markdown';
import { cn } from '../lib/utils';
import { format } from 'date-fns';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

import logo from '../assets/logo.png';

interface ReportProps {
  user: { displayName: string | null; email: string | null };
  results: UserResult[];
  feedback: Feedback[];
  questions: Question[];
  scores: { responsibility: number; selftrust: number; care: number };
  categoryDescriptions: Record<Category, string>;
  categoryShortDescriptions: Record<Category, string>;
  acknowledgmentText?: string;
  howToReadText?: string;
  scoreInsight?: string;
  showLogo?: boolean;
  pdfExcludeHistory?: boolean;
}

const CAT_LABEL: Record<Category, string> = { 
  responsibility: 'Responsibility', 
  selftrust: 'Self Trust', 
  care: 'Care', 
  overall: 'Overall',
  shadow_responsibility: 'Shadow Responsibility',
  shadow_selftrust: 'Shadow Self Trust',
  shadow_care: 'Shadow Care'
};
const CAT_COL: Record<Category, string> = { 
  responsibility: 'var(--cat-responsibility)', 
  selftrust: 'var(--cat-selftrust)', 
  care: 'var(--cat-care)', 
  overall: 'var(--cat-overall)',
  shadow_responsibility: 'var(--cat-responsibility)',
  shadow_selftrust: 'var(--cat-selftrust)',
  shadow_care: 'var(--cat-care)'
};

export const Report: React.FC<ReportProps> = ({ 
  user, results, feedback, questions, scores, categoryDescriptions, categoryShortDescriptions, acknowledgmentText, howToReadText, scoreInsight, showLogo = true, pdfExcludeHistory = false
}) => {
  const overallScore = (scores.responsibility + scores.selftrust + scores.care) / 3;
  const lastResult = results[0];
  
  const getFeedbackForScore = (cat: Category, score: number) => {
    if (cat === 'overall') {
      return feedback.find(f => {
        if (f.category !== 'overall') return false;
        
        // Check overall score range
        const matchesOverall = score >= f.minScore && score <= f.maxScore;
        if (!matchesOverall) return false;

        // Check category specific ranges if they are defined
        const matchesResponsibility = (f.responsibilityMin ?? 0) <= scores.responsibility && scores.responsibility <= (f.responsibilityMax ?? 5);
        const matchesSelfTrust = (f.selftrustMin ?? 0) <= scores.selftrust && scores.selftrust <= (f.selftrustMax ?? 5);
        const matchesCare = (f.careMin ?? 0) <= scores.care && scores.care <= (f.careMax ?? 5);

        return matchesResponsibility && matchesSelfTrust && matchesCare;
      });
    }
    return feedback.find(f => f.category === cat && score >= f.minScore && score <= f.maxScore);
  };

  return (
    <div id="report-content" className="w-[800px] min-h-[1100px] font-sans pb-20 bg-white text-gray-900">
      {/* Header */}
      <div id="pdf-header" className="flex justify-between items-start pb-8 mb-8 border-b border-gray-200">
        <div className="flex items-center gap-6">
          <img 
            src={logo} 
            alt="The Insourcing Way" 
            className="h-16 w-auto" 
            referrerPolicy="no-referrer"
            crossOrigin="anonymous"
          />
          <div className="space-y-1">
            <p className="text-[0.6rem] tracking-[0.3em] uppercase font-bold text-gray-400">The Insourcing Way</p>
            <h1 className="text-3xl font-light tracking-[0.2em] uppercase text-gray-900">Agency Report</h1>
            <p className="text-[0.65rem] tracking-widest uppercase text-gray-500">Personalized Assessment Summary</p>
          </div>
        </div>
        <div className="text-right">
          <p className="font-medium text-gray-900">{user.displayName || 'User'}</p>
          <p className="text-xs text-gray-500">{user.email}</p>
          <p className="text-xs mt-1 text-gray-500">{format(new Date(), 'MMMM d, yyyy')}</p>
        </div>
      </div>

      {/* Main Score Section - Simplified */}
      <div id="pdf-section-scores" className="mb-12 pb-12 pdf-section">
        <div className="space-y-8 max-w-2xl mx-auto">
          <div className="text-center space-y-2 pb-8 border-b border-gray-200">
            <p className="text-[0.65rem] tracking-[0.3em] uppercase font-bold text-gray-400">Agency Score</p>
            <p className="text-5xl font-extralight" style={{ color: CAT_COL.overall }}>
              {(scores.responsibility + scores.selftrust + scores.care).toFixed(1)}
              <span className="text-lg ml-2 text-gray-400">/ 15</span>
            </p>
          </div>

          <h3 className="text-xs tracking-[0.2em] uppercase font-bold pb-2 text-gray-400 border-b border-gray-200">Agency Score Breakdown</h3>
          {(['responsibility', 'selftrust', 'care'] as Category[]).map(cat => (
            <div key={cat} className="space-y-3">
              <div className="flex justify-between items-end">
                <span className="text-[0.65rem] tracking-widest uppercase font-medium" style={{ color: CAT_COL[cat] }}>{CAT_LABEL[cat]}</span>
                <span className="text-lg font-light text-gray-900">{scores[cat].toFixed(1)} <span className="text-[0.6rem] text-gray-400">/ 5</span></span>
              </div>
              <div className="h-1.5 w-full rounded-full overflow-hidden bg-gray-100">
                <div 
                  className="h-full transition-all duration-1000" 
                  style={{ 
                    width: `${(scores[cat] / 5) * 100}%`,
                    backgroundColor: CAT_COL[cat]
                  }} 
                />
              </div>
              {categoryShortDescriptions[cat] && (
                <p className="text-[0.65rem] leading-relaxed text-gray-600">
                  {categoryShortDescriptions[cat]}
                </p>
              )}
            </div>
          ))}

          {/* Venn Diagram */}
          <div className="flex flex-col items-center pt-8 border-t border-gray-200">
            <h4 className="text-[0.65rem] tracking-[0.25em] uppercase font-bold text-gray-400 mb-4">Agency Relationship Map</h4>
            <div className="w-[300px] h-[300px]">
              <Visual scores={scores} lightMode={true} showLogo={showLogo} />
            </div>
          </div>
        </div>
      </div>

      {/* Acknowledgment & How to Read */}
      {(acknowledgmentText || howToReadText) && (
        <div id="pdf-section-intro" className="mb-12 p-8 rounded-2xl space-y-8 pb-12 pdf-section bg-gray-50 border border-gray-200">
          {acknowledgmentText && (
            <div className="space-y-3">
              <Markdown className="markdown-body text-sm leading-relaxed text-gray-900" content={acknowledgmentText} />
            </div>
          )}
          {howToReadText && (
            <div className="space-y-3">
              <h3 className="text-[0.65rem] tracking-[0.25em] uppercase font-bold text-gray-400">How to Read Your Results</h3>
              <Markdown className="markdown-body text-sm leading-relaxed text-gray-900" content={howToReadText} />
            </div>
          )}
        </div>
      )}

      {/* Your Insights */}
      <div className="space-y-6">
        {(['overall', 'responsibility', 'selftrust', 'care'] as Category[]).map((cat, index) => {
          const score = cat === 'overall' ? overallScore : scores[cat as keyof typeof scores];
          const insight = getFeedbackForScore(cat, score);
          const description = categoryDescriptions[cat];
          
          return (
            <div key={cat} id={`pdf-section-insight-${cat}`} className="pt-6 pb-12 pdf-section pdf-insight-section">
              {index === 0 && (
                <div className="mb-8">
                  <h3 className="text-xs tracking-[0.2em] uppercase font-bold pb-2 text-gray-400 border-b border-gray-200">Your Insights</h3>
                </div>
              )}
              <div className="p-6 rounded-2xl space-y-3 bg-gray-50 border border-gray-100" style={{ borderLeft: `4px solid ${CAT_COL[cat]}` }}>
                <div className="flex justify-between items-center">
                  <h4 className="text-[0.6rem] tracking-widest uppercase font-bold" style={{ color: CAT_COL[cat] }}>{CAT_LABEL[cat]} Guidance</h4>
                  <span className="text-[0.6rem] font-medium text-gray-400">Score {score.toFixed(1)}</span>
                </div>
                {description && <Markdown className="markdown-body text-[0.7rem] leading-relaxed text-gray-600 mb-2" content={description} />}
                <div className="mt-4 p-4 rounded-xl bg-white border border-gray-200">
                  {insight?.title && (
                    <p className="text-[0.55rem] tracking-[0.2em] uppercase font-bold mb-2" style={{ color: CAT_COL[cat] }}>{insight.title}</p>
                  )}
                  {insight?.text ? (
                    <Markdown className="markdown-body text-sm leading-relaxed mb-4 text-gray-900" content={insight.text} />
                  ) : cat === 'overall' ? (
                    <p className="text-sm leading-relaxed mb-4 text-gray-900">
                      Your overall agency is a reflection of how you balance responsibility, self-trust, and care in your daily life.
                    </p>
                  ) : null}
                  <p className="text-[0.55rem] tracking-[0.2em] uppercase font-bold mb-2" style={{ color: CAT_COL[cat] }}>Level Up Advice</p>
                  <div className="space-y-4">
                    {insight?.levelUpText && (
                      <Markdown className="markdown-body text-[0.8rem] leading-relaxed text-gray-600" content={insight.levelUpText} />
                    )}

                    {(() => {
                      const shadowActions = questions
                        .filter(q => q.category === cat && q.isShadow)
                        .map(q => {
                          const answer = lastResult?.answers?.find(a => a.questionId === q.id);
                          const score = answer?.score || 0;
                          return { id: q.id, action: q.scoreFeedback?.[score]?.action };
                        })
                        .filter(item => !!item.action);

                      if (shadowActions.length === 0 && (insight?.levelUpText || cat === 'overall')) return null;
                      if (shadowActions.length === 0 && !insight?.levelUpText) {
                        return (
                          <div className="space-y-4">
                            <p className="text-[0.8rem] leading-relaxed text-gray-400">
                              No specific actions identified for your current score level.
                            </p>
                          </div>
                        );
                      }

                      return (
                        <div className="space-y-4">
                          <p className="text-[0.55rem] tracking-[0.2em] uppercase font-bold mb-2" style={{ color: CAT_COL[cat] }}>A note on over functioning</p>
                          {shadowActions.map(item => (
                            <Markdown key={item.id} className="markdown-body text-[0.8rem] leading-relaxed text-gray-600" content={item.action} />
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Detailed Question Breakdown */}
      <div id="pdf-section-details" className="mb-12 pdf-section no-pdf">
        <h3 className="text-xs tracking-[0.2em] uppercase font-bold pb-2 mb-6 text-gray-400 border-b border-gray-200">Detailed Question Breakdown</h3>
        <div className="space-y-8">
          {(['responsibility', 'selftrust', 'care'] as Category[]).map(cat => {
            const description = categoryDescriptions[cat];
            return (
              <div key={cat} className="space-y-4">
                <div className="space-y-1">
                  <h4 className="text-[0.65rem] tracking-widest uppercase font-bold" style={{ color: CAT_COL[cat] }}>{CAT_LABEL[cat]} Details</h4>
                  {description && <p className="text-[0.7rem] leading-relaxed italic text-gray-500">{description}</p>}
                </div>
                <div className="space-y-4">
                {(() => {
                  const resultIndex = results.findIndex(r => r.id === lastResult?.id);
                  const previousResult = results[resultIndex + 1];
                  
                  return questions.filter(q => q.category === cat).map(q => {
                    const answer = lastResult?.answers?.find(a => a.questionId === q.id);
                    const score = answer?.score || 0;
                    const scoreFeedback = q.scoreFeedback?.[score];

                    const prevAnswer = previousResult?.answers?.find(a => a.questionId === q.id);
                    const prevScore = prevAnswer?.score || 0;
                    const trend = prevScore > 0 ? score - prevScore : 0;

                    return (
                      <div key={q.id} className="p-4 rounded-xl space-y-3 border border-gray-200 bg-gray-50">
                        <div className="flex justify-between items-center gap-4">
                          <div className="flex-1 space-y-1">
                            <Markdown className="markdown-body text-[0.8rem] leading-relaxed text-gray-900" content={q.text} />
                            {previousResult && trend !== 0 && (
                              <p className="text-[0.55rem] font-bold uppercase tracking-tighter" style={{ color: trend > 0 ? 'var(--success)' : 'var(--error)' }}>
                                {trend > 0 ? '↑ Improving' : '↓ Declining'}
                              </p>
                            )}
                          </div>
                          <div className="text-center px-3 py-1 rounded-lg bg-white border border-gray-200">
                            <p className="text-[0.5rem] uppercase tracking-widest mb-0.5 text-gray-500">Score</p>
                            <p className="text-sm font-bold" style={{ color: CAT_COL[cat] }}>{score}</p>
                          </div>
                        </div>
                        
                        {(scoreFeedback?.feedback || scoreFeedback?.action) && (
                          <div className="grid grid-cols-2 gap-3 pt-3 mt-1 border-t border-dashed border-gray-200">
                            {scoreFeedback.feedback && (
                              <div className={cn("space-y-1", !scoreFeedback.action && "col-span-2")}>
                                <p className="text-[0.5rem] tracking-widest uppercase font-bold text-gray-500">Feedback</p>
                                <Markdown className="markdown-body text-[0.75rem] leading-relaxed text-gray-900" content={scoreFeedback.feedback} />
                              </div>
                            )}
                            {scoreFeedback.action && (
                              <div className={cn("space-y-1", !scoreFeedback.feedback && "col-span-2")}>
                                <p className="text-[0.5rem] tracking-widest uppercase font-bold" style={{ color: CAT_COL[cat] }}>Action</p>
                                <Markdown className="markdown-body text-[0.75rem] leading-relaxed text-gray-700" content={scoreFeedback.action} />
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          );
        })}
      </div>
    </div>

      {/* Progress History */}
      {results.length > 1 && !pdfExcludeHistory && (
        <div id="pdf-section-history" className="mb-12 pb-12 pdf-section">
          <h3 className="text-xs tracking-[0.2em] uppercase font-bold pb-2 mb-6 text-gray-400 border-b border-gray-200">Agency Progress Trend</h3>
          
          <div className="mb-8 p-6 rounded-xl bg-gray-50 border border-gray-200">
            <div style={{ width: '100%', height: 240 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={[...results].reverse().map(res => ({
                    date: format(res.timestamp.toDate(), 'MMM d'),
                    total: parseFloat(((res.scores.responsibility + res.scores.selftrust + res.scores.care) / 3).toFixed(1)),
                    responsibility: res.scores.responsibility,
                    selftrust: res.scores.selftrust,
                    care: res.scores.care
                  }))}
                  margin={{ top: 5, right: 30, left: 0, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                  <XAxis 
                    dataKey="date" 
                    axisLine={false} 
                    tickLine={false} 
                    tick={{ fill: '#6b7280', fontSize: 10 }} 
                    dy={10}
                  />
                  <YAxis 
                    domain={[0, 15]} 
                    axisLine={false} 
                    tickLine={false} 
                    tick={{ fill: '#6b7280', fontSize: 10 }} 
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#ffffff', 
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '14px',
                      color: '#111827'
                    }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="total" 
                    stroke="var(--cat-overall)" 
                    strokeWidth={3} 
                    dot={{ fill: 'var(--cat-overall)', r: 4 }} 
                    activeDot={{ r: 6 }} 
                    name="Total Agency"
                  />
                  <Line type="monotone" dataKey="responsibility" stroke={CAT_COL.responsibility} strokeWidth={1} dot={false} opacity={0.4} name="Responsibility" />
                  <Line type="monotone" dataKey="selftrust" stroke={CAT_COL.selftrust} strokeWidth={1} dot={false} opacity={0.4} name="Self Trust" />
                  <Line type="monotone" dataKey="care" stroke={CAT_COL.care} strokeWidth={1} dot={false} opacity={0.4} name="Care" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          <h3 className="text-xs tracking-[0.2em] uppercase font-bold pb-2 mb-6 text-gray-400 border-b border-gray-200">Assessment History</h3>
          <div className="overflow-hidden rounded-xl border border-gray-200">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="uppercase bg-gray-50 text-gray-500" style={{ fontSize: '0.6rem', letterSpacing: '0.1em' }}>
                  <th className="px-6 py-3 font-medium">Date</th>
                  <th className="px-6 py-3 font-medium">Total</th>
                  <th className="px-6 py-3 font-medium">Resp.</th>
                  <th className="px-6 py-3 font-medium">Trust</th>
                  <th className="px-6 py-3 font-medium">Care</th>
                </tr>
              </thead>
              <tbody className="text-gray-700">
                {results.slice(0, 5).map((res, i) => (
                  <tr key={res.id || i} className="border-t border-gray-200 bg-white">
                    <td className="px-6 py-4 font-medium">{format(res.timestamp.toDate(), 'MMM d, yyyy')}</td>
                    <td className="px-6 py-4 font-bold text-gray-900">{(res.scores.responsibility + res.scores.selftrust + res.scores.care).toFixed(1)}</td>
                    <td className="px-6 py-4">{res.scores.responsibility.toFixed(1)}</td>
                    <td className="px-6 py-4">{res.scores.selftrust.toFixed(1)}</td>
                    <td className="px-6 py-4">{res.scores.care.toFixed(1)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {results.length > 5 && (
            <p className="text-[0.6rem] mt-2 text-right italic text-gray-400">Showing last 5 assessments</p>
          )}
        </div>
      )}

      {/* Footer */}
      <div className="mt-auto pt-12 text-center border-t border-gray-200">
        <p className="text-[0.6rem] tracking-[0.3em] uppercase text-gray-400">The Insourcing Way • Agency Assessment</p>
      </div>
    </div>
  );
};
